/**
 * Email Connector configuration component.
 * Specialized configuration for Email connector (Microsoft Graph / Gmail).
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import { OAuthConnectButton } from './OAuthConnectButton';

export interface EmailConnectorConfigProps {
  instanceId?: string;
  settingsJson?: string;
  isConnected?: boolean;
  onChange?: (settingsJson: string) => void;
  onConnected?: () => void;
}

/**
 * Email Connector configuration component.
 */
export const EmailConnectorConfig = ({
  instanceId,
  settingsJson = '{}',
  isConnected = false,
  onChange,
  onConnected,
}: EmailConnectorConfigProps) => {
  const [provider, setProvider] = useState<'microsoft' | 'gmail'>('microsoft');
  const [defaultFolder, setDefaultFolder] = useState('inbox');
  const [defaultFromAddress, setDefaultFromAddress] = useState('');

  const handleProviderChange = (newProvider: 'microsoft' | 'gmail') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Email Provider Configuration
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select Provider
          </Typography>
          <ToggleButtonGroup
            value={provider}
            exclusive
            onChange={(_, newProvider) => newProvider && handleProviderChange(newProvider)}
            fullWidth
          >
            <ToggleButton value="microsoft">Microsoft Graph (Office 365)</ToggleButton>
            <ToggleButton value="gmail">Gmail API</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {provider === 'microsoft' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="Microsoft Graph"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Default Folder</InputLabel>
              <Select
                value={defaultFolder}
                onChange={(e) => {
                  setDefaultFolder(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultFolder = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
                label="Default Folder"
              >
                <MenuItem value="inbox">Inbox</MenuItem>
                <MenuItem value="sent">Sent Items</MenuItem>
                <MenuItem value="drafts">Drafts</MenuItem>
                <MenuItem value="archive">Archive</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info">
              Microsoft Graph requires OAuth 2.0 authentication with Mail.Read and Mail.ReadWrite permissions.
            </Alert>
          </Box>
        )}

        {provider === 'gmail' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="Gmail"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <TextField
              label="Default Label"
              value={defaultFolder}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultFolder(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultLabel = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              fullWidth
              helperText="Default Gmail label/folder (e.g., INBOX, SENT)"
            />
            <Alert severity="info">
              Gmail API requires OAuth 2.0 authentication with gmail.readonly and gmail.compose scopes.
            </Alert>
          </Box>
        )}

        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Draft Settings
          </Typography>
          <TextField
            label="Default From Address"
            value={defaultFromAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDefaultFromAddress(e.target.value);
              const settings = JSON.parse(settingsJson || '{}');
              settings.defaultFromAddress = e.target.value;
              onChange?.(JSON.stringify(settings));
            }}
            fullWidth
            helperText="Default email address for sending drafts (optional)"
          />
        </Box>
      </CardContent>
    </Card>
  );
};
